/**
 * ScrollArea — a Radix ScrollArea with slim, rose-on-hover scrollbars. Give it
 * a constrained height (via style / className) and it scrolls its children.
 */
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import * as RadixScrollArea from "@radix-ui/react-scroll-area";

export interface ScrollAreaProps
  extends ComponentPropsWithoutRef<typeof RadixScrollArea.Root> {
  /** Scroll axes to show scrollbars for. @default "vertical" */
  readonly orientation?: "vertical" | "horizontal" | "both";
  readonly children: ReactNode;
  /** Extra class on the inner viewport. */
  readonly viewportClassName?: string;
}

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  function ScrollArea(
    { orientation = "vertical", className, viewportClassName, children, ...rest },
    ref,
  ) {
    const showVertical = orientation === "vertical" || orientation === "both";
    const showHorizontal = orientation === "horizontal" || orientation === "both";
    return (
      <RadixScrollArea.Root
        ref={ref}
        className={["lc-scroll", className].filter(Boolean).join(" ")}
        {...rest}
      >
        <RadixScrollArea.Viewport
          className={["lc-scroll__viewport", viewportClassName].filter(Boolean).join(" ")}
        >
          {children}
        </RadixScrollArea.Viewport>
        {showVertical ? (
          <RadixScrollArea.Scrollbar className="lc-scroll__scrollbar" orientation="vertical">
            <RadixScrollArea.Thumb className="lc-scroll__thumb" />
          </RadixScrollArea.Scrollbar>
        ) : null}
        {showHorizontal ? (
          <RadixScrollArea.Scrollbar className="lc-scroll__scrollbar" orientation="horizontal">
            <RadixScrollArea.Thumb className="lc-scroll__thumb" />
          </RadixScrollArea.Scrollbar>
        ) : null}
        <RadixScrollArea.Corner />
      </RadixScrollArea.Root>
    );
  },
);
